import { zodResolver } from '@hookform/resolvers/zod';
import { not } from 'ramda';
import React, { FC, ReactNode, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import Pill from '@ui/atoms/Pill';
import SelectableButton from '@ui/atoms/SelectableButton';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';
import TextLink from '@ui/atoms/TextLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import spaced from '@ui/styled/Spaced';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

import { pharmacyDataConstants } from '../PharmacySelectionStep';
import { dataConstants, schema } from './MedicationDeliveryStep.data';

interface IFolxShippingContent {
	shippingHeader: string;
	shippingDescription: string;
	shippingPrice: string;
	shippingPriceInterval: string;
	enabled: boolean;
}

interface ICapsuleDeliveryContent {
	capsuleHeader: string;
	capsuleDescription: string;
	membershipPrice: string;
	membershipPriceInterval: string;
	copayPrice: string;
	copayPriceDescription: string;
	enabled: boolean;
	capsuleLogo: string;
}

interface IPreferredPharmacyContent {
	pharmacyHeader: string;
	pharmacyDescription: string;
}

export interface IMedicationDeliveryStepPageContent {
	heading: string;
	subHeading: string;
	alternatePageInfo: string;
	capsuleDisclaimer: string;
	capsuleInfoLinkText: string;
	capsuleInfoLinkUrl: string;
	insuranceAccepted: string;
	folxShipping: IFolxShippingContent;
	capsuleDelivery: ICapsuleDeliveryContent;
	preferredPharmacy: IPreferredPharmacyContent;
}

interface IMedicationDeliveryStepProps extends IFlowStepComponentProps {
	pageContent: IMedicationDeliveryStepPageContent;
	showCapsuleOption?: boolean;
}

const SpacedHeadingText = spaced(HeadingText);
const SpacedBasicText = spaced(BasicText);
const SpacedGridItem = spaced(CenteredGridItem);

const CapsuleLogoWrapper = styled.div`
	margin-bottom: ${theme.spacing.lg};
	width: 13rem;

	img {
		width: 100%;
	}
`;

const FooterContent = styled(FlexColumn)`
	align-items: flex-start;

	${TextLink}:first-child {
		margin-bottom: ${theme.spacing.md};
	}
`;
const SpacedFooterContent = spaced(FooterContent);

const MedicationDeliveryStep: FC<IMedicationDeliveryStepProps> = ({
	pageContent,
	stepKey,
	showCapsuleOption = true,
}) => {
	const {
		heading,
		subHeading,
		capsuleDisclaimer,
		capsuleInfoLinkText,
		capsuleInfoLinkUrl,
		folxShipping,
		capsuleDelivery,
		preferredPharmacy,
		insuranceAccepted,
		alternatePageInfo,
	} = pageContent;

	const { existingFormData, handleFormSubmission, handleNavBack, hideNavBack, flowId } =
		useContext(FlowContext);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			[dataConstants.MEDICATION_DELIVERY_METHOD]:
				existingFormData[dataConstants.MEDICATION_DELIVERY_METHOD],
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const { handleSubmit, control } = form;

	const onSubmit = (data: Record<string, unknown>) => {
		const isPharmacy =
			data[dataConstants.MEDICATION_DELIVERY_METHOD] === dataConstants.PHARMACY_METHOD;
		const formData = isPharmacy ? data : { ...data, [pharmacyDataConstants.PHARMACY_ID]: '' };

		handleFormSubmission(formData, stepKey);
	};

	const getFolxDeliveryCard = (content: IFolxShippingContent): ReactNode => {
		const headerContent = <BasicText variant="field">{content.shippingHeader}</BasicText>;

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content.shippingDescription }} />
		);

		return <BaseCard header={headerContent} body={bodyContent} borderless />;
	};

	const getCapsuleDeliveryCard = (content: ICapsuleDeliveryContent): ReactNode => {
		const headerContent = (
			<div>
				<CapsuleLogoWrapper>
					<img src={content.capsuleLogo} alt="capsule logo"></img>
				</CapsuleLogoWrapper>
				<BasicText variant="field">{content.capsuleHeader}</BasicText>
			</div>
		);

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content.capsuleDescription }} />
		);

		const footerContent = <Pill color="yellow" title={insuranceAccepted} padding="sm" />;

		return (
			<BaseCard
				header={headerContent}
				body={bodyContent}
				footer={footerContent}
				footerMargin="smmd"
				borderless
			/>
		);
	};

	const getPreferredPharmacyCard = (content: IPreferredPharmacyContent): ReactNode => {
		const headerContent = <BasicText variant="field">{content.pharmacyHeader}</BasicText>;

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content.pharmacyDescription }} />
		);

		const footerContent = <Pill color="yellow" title={insuranceAccepted} padding="sm" />;

		return (
			<BaseCard
				header={headerContent}
				body={bodyContent}
				footer={footerContent}
				footerMargin="smmd"
				borderless
			/>
		);
	};

	return (
		<>
			<SpacedGridItem marginBottom="lgxl">
				<SpacedHeadingText marginBottom="sm">{heading}</SpacedHeadingText>
				<BasicText>
					{flowId === 'vhc_sub' || flowId === 'mh_sub' ? alternatePageInfo : subHeading}
				</BasicText>
			</SpacedGridItem>
			<SpacedGridItem marginBottom="xxxl">
				<form onSubmit={handleSubmit(onSubmit)}>
					<Controller
						name={dataConstants.MEDICATION_DELIVERY_METHOD}
						control={control}
						render={({ field: { value, ...field } }) => (
							<SelectableButtonGroup
								layout="column"
								aria-label={dataConstants.MEDICATION_DELIVERY_METHOD}>
								{flowId !== 'vhc_sub' && flowId !== 'mh_sub' && (
									<SelectableButton
										alignment={'start'}
										optionValue={dataConstants.FOLX_METHOD}
										optionLabel={folxShipping.shippingHeader}
										key={dataConstants.FOLX_METHOD}
										value={value ?? ''}
										{...field}>
										{getFolxDeliveryCard(folxShipping)}
									</SelectableButton>
								)}
								{showCapsuleOption && (
									<SelectableButton
										alignment={'start'}
										optionValue={dataConstants.CAPSULE_METHOD}
										optionLabel={capsuleDelivery.capsuleHeader}
										key={dataConstants.CAPSULE_METHOD}
										value={value ?? ''}
										{...field}>
										{getCapsuleDeliveryCard(capsuleDelivery)}
									</SelectableButton>
								)}
								{(flowId === 'hrt_t_sub' ||
									flowId === 'hrt_e_sub' ||
									flowId === 'vhc_sub' ||
									flowId === 'mh_sub') && (
									<SelectableButton
										alignment={'start'}
										optionValue={dataConstants.PHARMACY_METHOD}
										optionLabel={preferredPharmacy.pharmacyHeader}
										key={dataConstants.PHARMACY_METHOD}
										value={value ?? ''}
										{...field}>
										{getPreferredPharmacyCard(preferredPharmacy)}
									</SelectableButton>
								)}
							</SelectableButtonGroup>
						)}
					/>
					<SpacedFooterContent marginTop="lg">
						{showCapsuleOption && (
							<TextLink label={capsuleInfoLinkText} href={capsuleInfoLinkUrl} />
						)}
						<SpacedBasicText variant="fineprint" marginBottom="xxl">
							{capsuleDisclaimer}
						</SpacedBasicText>
					</SpacedFooterContent>
					<NavigationSection>
						{not(hideNavBack) && <Button label="Back" onClick={() => handleNavBack(stepKey)} />}
						<Button variant="secondary" label={hideNavBack ? 'Return' : 'Next'} type="submit" />
					</NavigationSection>
				</form>
			</SpacedGridItem>
		</>
	);
};

export default MedicationDeliveryStep;
